import { trpcClient } from '@/trpcClient';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import type { CityAndStateDto } from '@shelflix/trpc/routes/cityAndState/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@shelflix/ui-web/components/ui/dialog';
import { Label } from '@shelflix/ui-web/components/ui/label';
import { ScrollArea, ScrollBar } from '@shelflix/ui-web/components/ui/scroll-area';
import { Separator } from '@shelflix/ui-web/components/ui/separator';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GenericSelect } from '../GenericSelect';

interface SegmentationModalProps {
  trigger: React.ReactNode;
  onSaveSegmentation?: (locations: SelectedLocation[]) => void;
}

export interface SelectedLocation {
  uuid: string;
  name: string;
  isState: boolean;
  materialCount: number;
}

export function SegmentationModal({ trigger, onSaveSegmentation }: SegmentationModalProps) {
  const [open, setOpen] = useState(false);
  const [cityAndState, setCityAndState] = useState<CityAndStateDto | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<SelectedLocation[]>([]);
  const [totalMaterial, setTotalMaterial] = useState<number>(0);

  const addSegmentation = async () => {
    if (!cityAndState) return;

    try {
      const { count } = await trpcClient.material.getCount.query({
        cityOrStateIbgeCode: cityAndState.ibgeCode,
      });

      const newLocation: SelectedLocation = {
        uuid: cityAndState.uuid,
        name: cityAndState.name,
        isState: cityAndState.isState,
        materialCount: Number(count ?? 0),
      };

      if (!selectedLocations.find((loc) => loc.uuid === newLocation.uuid)) {
        setSelectedLocations((prev) => [...prev, newLocation]);
      }
      setCityAndState(null);
    } catch (error) {
      toast.error('Erro ao buscar quantidade de materiais:', error);
    }
  };

  const removeSegmentation = (location: SelectedLocation) => {
    setSelectedLocations((prev) => prev.filter((loc) => loc.uuid !== location.uuid));
  };

  useEffect(() => {
    const total = selectedLocations.reduce((acc, loc) => acc + loc.materialCount, 0);
    setTotalMaterial(total);
  }, [selectedLocations]);

  const onSave = () => {
    onSaveSegmentation?.(selectedLocations);
    setSelectedLocations([]);
    toast.success('Alteração salva com sucesso');
    setOpen(false);
  };

  const { isMobile } = useDeviceDetect();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="w-full border-none sm:min-w-[563px]"
        style={{
          padding: isMobile ? '48px 24px' : '48px',
        }}
      >
        <DialogHeader className="flex flex-col items-center gap-4">
          <DialogTitle className="text-primary flex items-center justify-center gap-4 font-semibold sm:flex-col">
            <div
              className="bg-secondary flex shrink-0 items-center justify-center rounded-sm"
              style={{ width: isMobile ? '48px' : '64px', height: isMobile ? '48px' : '64px' }}
            >
              <Icon name="device_hub" className="text-primary" size={isMobile ? 30 : 40} />
            </div>
            <div className="w-fit max-w-[50%] grow-0 leading-6 sm:max-w-full">Segmentações da campanha</div>
          </DialogTitle>
          <DialogDescription className="px-6 text-center">
            Selecione estados ou cidades para segmentar o disparo de campanha apenas para os dispositivos desejados.
          </DialogDescription>
        </DialogHeader>

        <div className="flex w-full flex-col gap-1 pb-8">
          <Label className="text-xs">Estado ou cidade</Label>
          <div className="flex flex-row gap-2">
            <GenericSelect<CityAndStateDto>
              onSelect={(value) => {
                setCityAndState(value);
              }}
              value={cityAndState}
              fetchItems={async (search) => {
                if (!search) return [];

                // Fetch directly via trpcClient to avoid stale data from useQuery cache
                const result = await trpcClient.cityAndState.list.query({
                  search: {
                    fields: ['name', 'abbreviation'],
                    value: search,
                  },
                });

                return result ?? [];
              }}
              renderItem={(item) => (
                <div>
                  <div className="text-sm font-medium">{item.name}</div>
                </div>
              )}
              getItemLabel={(brand) => brand.name}
              getItemKey={(item) => item.uuid}
            />
            <Button variant="ghost" className="bg-primary hover:bg-primary" onClick={addSegmentation}>
              <Icon name="add" className="text-white" />
            </Button>
          </div>
          <div>
            {selectedLocations.length > 0 && (
              <ScrollArea type="always" className="mt-2 flex-1 flex-col gap-2">
                <div className="flex max-h-80 flex-col gap-2">
                  {selectedLocations.map((location) => (
                    <div
                      key={location.uuid}
                      className="bg-secondary text-primary flex w-full items-center justify-between rounded-md p-2"
                    >
                      <div className="flex flex-col">
                        <span className="font-['Figtree',Helvetica] font-bold">{location.name}</span>
                        <span className="font-['Figtree',Helvetica] font-normal">
                          {location.materialCount} materiais
                        </span>
                      </div>
                      <Button variant="ghost" onClick={() => removeSegmentation(location)}>
                        <Icon name="close" className="text-primary" />
                      </Button>
                    </div>
                  ))}
                </div>
                <ScrollBar orientation="vertical" />
              </ScrollArea>
            )}
          </div>
        </div>

        <Separator className="border-2" />

        <div className="text-center sm:text-left">
          <span className="text-primary font-bold">Total: </span>
          <span className="text-primary">{totalMaterial} materiais</span>
        </div>

        <div className="flex grow-0 justify-center">
          <Button
            onClick={() => onSave()}
            className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
          >
            <Icon name="device_hub" className="text-white" />
            <span>Salvar segmentação</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
